import React from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Upload, Loader2, RotateCcw, Save, ExternalLink, Database, ChevronRight,
         LayoutTemplate, Newspaper, Package, Tag, Settings, Star, Ticket, Image as ImageIcon } from "lucide-react";
import { api, fileUrl, errorMessage, uploadImage, youtubeId } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CMS_DEFAULTS } from "@/lib/cms";
import { PROMISE_ICONS } from "@/components/HomeSections";

/*
  Admin → Content (CMS). Each section below is a form generated from a small description (SECTIONS).
  Saving stores the section in the database (PUT /api/admin/cms/{key}); "Reset" goes back to the built-in text.
*/
const ICON_OPTIONS = Object.keys(PROMISE_ICONS);

const SECTIONS = [
  { key: "home_intro", group: "Home page", title: "Home: Intro section (below the banner)", view: "/",
    fields: [
      { k: "enabled", label: "Show this section", type: "bool" },
      { k: "overline", label: "Small heading" },
      { k: "title", label: "Big heading", type: "textarea", rows: 2 },
      { k: "text", label: "Paragraph", type: "textarea" },
      { k: "signature", label: "Signed by" },
      { k: "signature_role", label: "Signature sub-title" },
      { k: "badge_label", label: "Badge on the picture — small text (e.g. Since)" },
      { k: "badge_value", label: "Badge on the picture — big text (e.g. 1978). Leave both empty to hide the badge" },
      { k: "image", label: "Picture", type: "image" },
      { k: "stats", label: "Animated numbers", type: "list", item: "number", max: 4, fields: [{ k: "value", label: "Number (e.g. 5000+)" }, { k: "label", label: "Label" }] },
      { k: "promises", label: "Promise cards", type: "list", item: "card", max: 8, fields: [{ k: "icon", label: "Icon", type: "select", options: ICON_OPTIONS }, { k: "title", label: "Title" }, { k: "text", label: "Text" }] },
    ] },
  { key: "home_sections", group: "Home page", title: "Home: Section headings", view: "/",
    fields: [
      { k: "categories_overline", label: "Shop by Categories — small heading" },
      { k: "categories_title", label: "Shop by Categories — heading" },
      { k: "limelight_overline", label: "Products in Limelight — small heading" },
      { k: "limelight_title", label: "Products in Limelight — heading" },
      { k: "limelight_button", label: "Products in Limelight — button text" },
    ],
    note: "Which products appear in “Products in Limelight”: tick “Featured” on a product (Admin → Products)." },
  { key: "home_journey", group: "Home page", title: "Home: Our Journey (timeline)", view: "/",
    fields: [
      { k: "enabled", label: "Show this section", type: "bool" },
      { k: "overline", label: "Small heading" },
      { k: "title", label: "Heading" },
      { k: "subtitle", label: "Sub-heading" },
      { k: "items", label: "Timeline", type: "list", item: "milestone", max: 12, fields: [{ k: "year", label: "Year (types itself out on scroll)" }, { k: "title", label: "Title" }, { k: "body", label: "Text", type: "textarea", rows: 2 }] },
    ],
    note: "The same timeline is also shown on the About page." },
  { key: "home_testimonials", group: "Home page", title: "Home: Testimonials", view: "/",
    fields: [
      { k: "enabled", label: "Show this section", type: "bool" },
      { k: "overline", label: "Small heading" },
      { k: "title", label: "Heading" },
      { k: "button_text", label: "Button text (leave empty to hide the button)" },
      { k: "button_link", label: "Button link (e.g. your Google reviews page)" },
      { k: "items", label: "Testimonials", type: "list", item: "testimonial", max: 9, fields: [{ k: "name", label: "Name" }, { k: "role", label: "City / role" }, { k: "rating", label: "Stars (1–5)", type: "number" }, { k: "text", label: "What they said", type: "textarea", rows: 3 }] },
    ] },
  { key: "about", group: "Pages", title: "About page", view: "/about",
    fields: [
      { k: "hero_overline", label: "Top — small heading" },
      { k: "hero_title", label: "Top — heading" },
      { k: "hero_subtitle", label: "Top — sub-heading", type: "textarea", rows: 2 },
      { k: "hero_image", label: "Top — picture", type: "image" },
      { k: "story_title", label: "Story — heading" },
      { k: "story_text", label: "Story — how the journey started (empty line = new paragraph)", type: "textarea", rows: 8 },
      { k: "story_image", label: "Story — picture", type: "image" },
      { k: "quote", label: "Quote", type: "textarea", rows: 2 },
      { k: "quote_author", label: "Quote by" },
      { k: "stats", label: "Numbers", type: "list", item: "number", max: 4, fields: [{ k: "value", label: "Number" }, { k: "label", label: "Label" }] },
      { k: "values", label: "Our values", type: "list", item: "value", max: 8, fields: [{ k: "title", label: "Title" }, { k: "text", label: "Text", type: "textarea", rows: 2 }] },
      { k: "video_url", label: "YouTube video link (leave empty to hide the video)", type: "youtube" },
      { k: "video_title", label: "Video — heading" },
      { k: "video_text", label: "Video — text" },
      { k: "gallery_title", label: "Gallery — heading" },
      { k: "gallery", label: "Gallery pictures", type: "images", max: 24 },
      { k: "faq_title", label: "FAQ — heading" },
      { k: "faqs", label: "FAQs", type: "list", item: "question", max: 30, fields: [{ k: "q", label: "Question" }, { k: "a", label: "Answer", type: "textarea", rows: 3 }] },
      { k: "cta_title", label: "Bottom box — heading" },
      { k: "cta_text", label: "Bottom box — text" },
    ] },
  { key: "lookbook", group: "Pages", title: "Lookbook page", view: "/lookbook",
    fields: [
      { k: "overline", label: "Small heading" },
      { k: "title", label: "Heading" },
      { k: "intro", label: "Introduction", type: "textarea", rows: 3 },
      { k: "looks", label: "Looks (styled outfits)", type: "list", item: "look", max: 30, fields: [
        { k: "image", label: "Picture", type: "image" }, { k: "title", label: "Name of the look" }, { k: "occasion", label: "Occasion (used for the filter buttons)" },
        { k: "description", label: "Description", type: "textarea", rows: 2 }, { k: "link", label: "Shop link (e.g. /shop/silk-sarees or /product/…)" }, { k: "link_text", label: "Link text" }] },
      { k: "tips_title", label: "Styling notes — heading" },
      { k: "tips", label: "Styling notes", type: "list", item: "note", max: 6, fields: [{ k: "title", label: "Title" }, { k: "text", label: "Text", type: "textarea", rows: 2 }] },
      { k: "cta_title", label: "Bottom — heading" },
      { k: "cta_text", label: "Bottom — text" },
    ] },
  { key: "whatsapp", group: "Site-wide", title: "WhatsApp button", view: "/",
    fields: [
      { k: "enabled", label: "Show the WhatsApp button", type: "bool" },
      { k: "phone", label: "WhatsApp number (leave empty to use the one in Settings)" },
      { k: "side", label: "Button position", type: "select", options: ["right", "left"] },
      { k: "title", label: "Chat window — name" },
      { k: "subtitle", label: "Chat window — status line" },
      { k: "greeting", label: "Greeting message", type: "textarea", rows: 2 },
      { k: "messages", label: "Ready-made messages (the order here is the order customers see)", type: "list", item: "message", max: 12, fields: [{ k: "label", label: "Button text" }, { k: "text", label: "Message sent on WhatsApp", type: "textarea", rows: 2 }] },
    ] },
  { key: "footer", group: "Site-wide", title: "Footer", view: "/",
    fields: [
      { k: "tagline", label: "Text under the logo", type: "textarea", rows: 2 },
      { k: "copyright", label: "Copyright line" },
    ] },
  { key: "auth", group: "Site-wide", title: "Sign in / Create account pages", view: "/login",
    fields: [
      { k: "login_title", label: "Sign in — heading" },
      { k: "login_subtitle", label: "Sign in — sub-heading" },
      { k: "register_title", label: "Create account — heading" },
      { k: "register_subtitle", label: "Create account — sub-heading" },
      { k: "quotes", label: "Rotating quotes", type: "strings", max: 8 },
      { k: "images", label: "Moving pictures", type: "images", max: 12 },
    ] },
];

const OTHER = [
  { to: "/admin/home-page", label: "Home banners, announcement bar, craft videos & reels", icon: LayoutTemplate },
  { to: "/admin/blog", label: "Blog posts", icon: Newspaper },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/categories", label: "Categories & Shop menu", icon: Tag },
  { to: "/admin/reviews", label: "Product reviews", icon: Star },
  { to: "/admin/coupons", label: "Coupons", icon: Ticket },
  { to: "/admin/settings", label: "Store details, contact, social links, payments", icon: Settings },
];

const input = "w-full border border-neutral-300 p-2.5 text-sm focus:outline-none focus:border-[#0E4D34] bg-white";
const label = "text-xs uppercase tracking-widest text-neutral-600 block mb-1.5";

function ImageField({ value, onChange, testid }) {
  const [busy, setBusy] = React.useState(false);
  const up = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setBusy(true);
    try { onChange(await uploadImage(file, "home")); } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <div className="flex gap-3 items-start">
      <div className="w-20 h-20 shrink-0 rounded-lg overflow-hidden bg-neutral-100 border flex items-center justify-center">
        {value ? <img src={fileUrl(value)} alt="" className="w-full h-full object-cover"/> : <ImageIcon size={18} className="text-neutral-300"/>}
      </div>
      <div className="flex-1 space-y-2 min-w-0">
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Upload a picture or paste an image link" className={input} data-testid={testid}/>
        <div className="flex gap-3 items-center">
          <label className="text-xs border px-3 py-1.5 cursor-pointer hover:border-[#0E4D34] inline-flex items-center gap-1.5">
            {busy ? <Loader2 size={12} className="animate-spin"/> : <Upload size={12}/>}Upload
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={up}/>
          </label>
          {value && <button type="button" onClick={() => onChange("")} className="text-xs text-[#991B1B]">Remove</button>}
        </div>
      </div>
    </div>
  );
}

function ImagesField({ value, onChange, max = 24 }) {
  const list = Array.isArray(value) ? value : [];
  const [busy, setBusy] = React.useState(false);
  const up = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of files.slice(0, Math.max(0, max - list.length))) urls.push(await uploadImage(f, "home"));
      onChange([...list, ...urls]);
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const move = (i, d) => { const n = [...list]; const j = i + d; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; onChange(n); };
  return (
    <div>
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
        {list.map((src, i) => (
          <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border bg-neutral-100">
            <img src={fileUrl(src)} alt="" className="w-full h-full object-cover"/>
            <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/55 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
              <button type="button" onClick={() => move(i, -1)} className="p-1 text-white" aria-label="Move left"><ArrowUp size={12} className="-rotate-90"/></button>
              <button type="button" onClick={() => onChange(list.filter((_, k) => k !== i))} className="p-1 text-white" aria-label="Delete picture"><Trash2 size={12}/></button>
              <button type="button" onClick={() => move(i, 1)} className="p-1 text-white" aria-label="Move right"><ArrowDown size={12} className="-rotate-90"/></button>
            </div>
          </div>
        ))}
        {list.length < max && (
          <label className="aspect-square rounded-lg border-2 border-dashed border-neutral-300 hover:border-[#0E4D34] flex flex-col items-center justify-center text-xs text-neutral-500 cursor-pointer">
            {busy ? <Loader2 size={18} className="animate-spin"/> : <><Plus size={18}/>Add</>}
            <input type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={up}/>
          </label>
        )}
      </div>
      <div className="text-[11px] text-neutral-400 mt-1">{list.length}/{max} pictures · hover a picture to move or delete it</div>
    </div>
  );
}

function StringsField({ value, onChange, max = 8 }) {
  const list = Array.isArray(value) ? value : [];
  return (
    <div className="space-y-2">
      {list.map((v, i) => (
        <div key={i} className="flex gap-2">
          <input value={v} onChange={(e) => onChange(list.map((x, k) => (k === i ? e.target.value : x)))} className={input}/>
          <button type="button" onClick={() => onChange(list.filter((_, k) => k !== i))} className="text-[#991B1B] px-2" aria-label="Delete"><Trash2 size={15}/></button>
        </div>
      ))}
      {list.length < max && <button type="button" onClick={() => onChange([...list, ""])} className="text-xs border px-3 py-1.5 hover:border-[#0E4D34] inline-flex items-center gap-1"><Plus size={12}/>Add</button>}
    </div>
  );
}

function ListField({ field, value, onChange }) {
  const list = Array.isArray(value) ? value : [];
  const set = (i, k, v) => onChange(list.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const move = (i, d) => { const n = [...list]; const j = i + d; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; onChange(n); };
  const blank = Object.fromEntries(field.fields.map((f) => [f.k, f.type === "number" ? 5 : f.type === "select" ? f.options[0] : ""]));
  return (
    <div className="space-y-3" data-testid={`cms-list-${field.k}`}>
      {list.map((it, i) => (
        <div key={i} className="border bg-neutral-50/70 rounded-lg p-3 sm:p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-semibold text-neutral-700">#{i + 1} {field.item}</div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 border bg-white disabled:opacity-30" aria-label="Move up"><ArrowUp size={13}/></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === list.length - 1} className="p-1.5 border bg-white disabled:opacity-30" aria-label="Move down"><ArrowDown size={13}/></button>
              <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} className="p-1.5 border bg-white text-[#991B1B]" aria-label="Delete" data-testid={`cms-del-${field.k}-${i}`}><Trash2 size={13}/></button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {field.fields.map((f) => (
              <div key={f.k} className={f.type === "textarea" || f.type === "image" ? "sm:col-span-2" : ""}>
                <label className={label}>{f.label}</label>
                <FieldInput f={f} value={it[f.k]} onChange={(v) => set(i, f.k, v)}/>
              </div>
            ))}
          </div>
        </div>
      ))}
      {list.length < (field.max || 50) && (
        <button type="button" onClick={() => onChange([...list, blank])} className="text-xs border px-3 py-2 bg-white hover:border-[#0E4D34] inline-flex items-center gap-1" data-testid={`cms-add-${field.k}`}>
          <Plus size={12}/>Add {field.item}
        </button>
      )}
    </div>
  );
}

function FieldInput({ f, value, onChange, testid }) {
  switch (f.type) {
    case "textarea": return <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={f.rows || 4} className={input} data-testid={testid}/>;
    case "bool": return (
      <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={value !== false} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#0E4D34]" data-testid={testid}/>{value !== false ? "Shown" : "Hidden"}
      </label>);
    case "select": return <select value={value || f.options[0]} onChange={(e) => onChange(e.target.value)} className={input} data-testid={testid}>{f.options.map((o) => <option key={o} value={o}>{o}</option>)}</select>;
    case "number": return <input type="number" min={1} max={5} value={value ?? 5} onChange={(e) => onChange(Number(e.target.value))} className={input}/>;
    case "image": return <ImageField value={value} onChange={onChange} testid={testid}/>;
    case "images": return <ImagesField value={value} onChange={onChange} max={f.max}/>;
    case "strings": return <StringsField value={value} onChange={onChange} max={f.max}/>;
    case "youtube": {
      const id = youtubeId(value);
      return (
        <div className="space-y-2">
          <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://www.youtube.com/watch?v=…" className={input} data-testid={testid}/>
          {value && !id && <div className="text-xs text-[#991B1B]">That doesn't look like a YouTube link.</div>}
          {id && <img src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`} alt="" className="w-48 aspect-video object-cover rounded border"/>}
        </div>
      );
    }
    default: return <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={input} data-testid={testid}/>;
  }
}

function SectionEditor({ section }) {
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({ queryKey: ["cms-admin", section.key], queryFn: () => api.get(`/cms/${section.key}`).then((r) => r.data) });
  const [form, setForm] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    if (data) setForm({ ...CMS_DEFAULTS[section.key], ...(data.data || {}) });
  }, [data, section.key]);
  if (isLoading || !form) return <div className="p-6 text-sm text-neutral-500">Loading…</div>;
  const set = (k) => (v) => setForm((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/cms/${section.key}`, { data: form });
      toast.success("Saved — the website is updated");
      qc.invalidateQueries({ queryKey: ["cms", section.key] });
      refetch();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Go back to the original built-in content for this section? Your changes here will be removed.")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/cms/${section.key}`);
      toast.success("Back to the original content");
      qc.invalidateQueries({ queryKey: ["cms", section.key] });
      await refetch();
      setForm({ ...CMS_DEFAULTS[section.key] });
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border" data-testid={`cms-editor-${section.key}`}>
      <div className="p-4 sm:p-5 border-b flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold text-lg">{section.title}</h2>
          <div className="text-xs text-neutral-500 mt-0.5">{data?.updated_at ? `Last saved ${new Date(data.updated_at).toLocaleString()}` : "Showing the original built-in content (not changed yet)"}</div>
        </div>
        <div className="flex gap-2">
          <a href={section.view} target="_blank" rel="noreferrer" className="text-xs border px-3 py-2 inline-flex items-center gap-1 hover:border-[#0E4D34]"><ExternalLink size={12}/>View on site</a>
          {data?.data && <button onClick={reset} disabled={busy} className="text-xs border px-3 py-2 inline-flex items-center gap-1 hover:border-[#991B1B] text-[#991B1B]"><RotateCcw size={12}/>Reset</button>}
          <button onClick={save} disabled={busy} className="btn-silk !py-2 disabled:opacity-50" data-testid="cms-save">{busy ? <Loader2 size={13} className="animate-spin mr-1.5"/> : <Save size={13} className="mr-1.5"/>}Save</button>
        </div>
      </div>
      {section.note && <div className="mx-4 sm:mx-5 mt-4 text-xs bg-[#E8F3EE] text-[#0E4D34] p-3">{section.note}</div>}
      <div className="p-4 sm:p-5 space-y-5">
        {section.fields.map((f) => (
          <div key={f.k}>
            <label className={label}>{f.label}</label>
            {f.type === "list"
              ? <ListField field={f} value={form[f.k]} onChange={set(f.k)}/>
              : <FieldInput f={f} value={form[f.k]} onChange={set(f.k)} testid={`cms-${section.key}-${f.k}`}/>}
          </div>
        ))}
      </div>
      <div className="p-4 sm:p-5 border-t flex justify-end">
        <button onClick={save} disabled={busy} className="btn-silk disabled:opacity-50">{busy ? "Saving…" : "Save changes"}</button>
      </div>
    </div>
  );
}

function DemoData() {
  const qc = useQueryClient();
  const { data, refetch } = useQuery({ queryKey: ["adm-demo"], queryFn: () => api.get("/admin/demo-data").then((r) => r.data) });
  const [busy, setBusy] = React.useState(false);
  const total = data ? Object.values(data).reduce((a, b) => a + b, 0) : 0;
  const run = async (method) => {
    if (method === "delete" && !window.confirm("Remove all demo products, demo customers, demo reviews, demo blog posts and demo reels? Your own data is not touched.")) return;
    setBusy(true);
    try {
      const r = await api[method]("/admin/demo-data");
      const n = Object.entries(r.data).filter(([, v]) => v).map(([k, v]) => `${v} ${k}`).join(", ");
      toast.success(method === "delete" ? `Removed ${n || "nothing"}` : `Added ${n || "nothing (already there)"}`);
      refetch();
      qc.invalidateQueries();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border p-5" data-testid="demo-data">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-full bg-[#FFF7E0] text-[#8a6d00] flex items-center justify-center shrink-0"><Database size={18}/></span>
        <div className="flex-1">
          <h2 className="font-semibold">Demo data</h2>
          <p className="text-sm text-neutral-600 mt-1">Sample products (with stock), customers, reviews, blog posts and reels so the shop looks full while you add your own. Remove them before going live.</p>
          {data && (
            <div className="flex flex-wrap gap-2 mt-3">
              {Object.entries(data).map(([k, v]) => <span key={k} className="text-xs bg-neutral-100 px-2.5 py-1 rounded-full">{v} {k}</span>)}
            </div>
          )}
          <div className="flex gap-2 mt-4 flex-wrap">
            <button onClick={() => run("post")} disabled={busy} className="text-xs border px-3 py-2 hover:border-[#0E4D34] disabled:opacity-50">Add demo data</button>
            <button onClick={() => run("delete")} disabled={busy || !total} className="text-xs border px-3 py-2 text-[#991B1B] hover:border-[#991B1B] disabled:opacity-40" data-testid="demo-remove">Remove all demo data</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminCMS() {
  const { user } = useAuth();
  const [active, setActive] = React.useState(SECTIONS[0].key);
  if (user?.role !== "super_admin") return <div className="bg-white border p-8 text-sm text-neutral-600">Only the Super Admin can edit website content.</div>;
  const section = SECTIONS.find((s) => s.key === active);
  const groups = [...new Set(SECTIONS.map((s) => s.group))];
  return (
    <div data-testid="admin-cms">
      <div className="mb-6">
        <h1 className="h2-editorial">Website content</h1>
        <p className="text-sm text-neutral-500 mt-1">Change the text, pictures, videos and messages shown on the website. Pick a section, edit, then press Save.</p>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-6 items-start">
        <div className="space-y-4 lg:sticky lg:top-6">
          {groups.map((g) => (
            <div key={g} className="bg-white border">
              <div className="px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-neutral-500 border-b">{g}</div>
              {SECTIONS.filter((s) => s.group === g).map((s) => (
                <button key={s.key} onClick={() => setActive(s.key)} data-testid={`cms-tab-${s.key}`}
                        className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between gap-2 border-l-2 ${active === s.key ? "border-[#0E4D34] bg-[#E8F3EE] text-[#0E4D34] font-semibold" : "border-transparent hover:bg-neutral-50"}`}>
                  {s.title.replace(/^Home: /, "")}<ChevronRight size={14} className="opacity-50"/>
                </button>
              ))}
            </div>
          ))}
          <div className="bg-white border">
            <div className="px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-neutral-500 border-b">Managed on other pages</div>
            {OTHER.map((o) => (
              <Link key={o.to} to={o.to} className="flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-neutral-50">
                <o.icon size={14} className="text-[#0E4D34] shrink-0"/>{o.label}
              </Link>
            ))}
          </div>
          <DemoData/>
        </div>
        <SectionEditor key={section.key} section={section}/>
      </div>
    </div>
  );
}
